import React from "react"
import { Box, Stack, Tooltip } from "@mui/material"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked"
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import { colors } from "../theme/tokens"
import { resolveViraPalette } from "../theme/resolveViraPalette"
import { Heading, ViraText } from "../atoms/ViraText"
import { ViraLink } from "../atoms/ViraLink"
import { ContentCard } from "./ContentCard"

export type InputReadyState = "ready" | "missing" | "in_progress" | "disabled"

export type InputStatusItem = {
  id: string
  title: string
  status: InputReadyState
  detail: string
  /** Hover explanation (e.g. what App data is and where it comes from). */
  tooltip?: string
  actionLabel?: string
  onAction?: () => void
}

export type InputStatusRowProps = {
  items: InputStatusItem[]
  readyCount: number
  totalCount: number
}

function StatusIcon({ status }: { status: InputReadyState }) {
  if (status === "ready") {
    return (
      <CheckCircleOutlineIcon
        sx={{ fontSize: 14, color: colors.graphs.alerts.positive }}
      />
    )
  }
  if (status === "in_progress") {
    return (
      <HourglassEmptyIcon
        sx={(theme) => {
          const vira = resolveViraPalette(theme)
          return { fontSize: 14, color: vira.accent }
        }}
      />
    )
  }
  return (
    <RadioButtonUncheckedIcon
      sx={(theme) => {
        const vira = resolveViraPalette(theme)
        return { fontSize: 14, color: vira.subtle }
      }}
    />
  )
}

/** Session Status grid (app data, transcript, plan, note) for Loop Pro sessions. */
export function InputStatusRow({
  items,
  readyCount,
  totalCount,
}: InputStatusRowProps) {
  return (
    <ContentCard
      title="Session Status"
      meta={
        <ViraText tone="subtle" sx={{ fontSize: 12 }}>
          {readyCount} of {totalCount} ready
        </ViraText>
      }
      bodySx={{ px: 0, py: 0 }}
    >
      <Stack direction={{ xs: "column", md: "row" }}>
        {items.map((item, i) => (
          <Box
            key={item.id}
            sx={(theme) => {
              const vira = resolveViraPalette(theme)
              return {
                flex: 1,
                px: 2.25,
                py: 1.5,
                borderRight: {
                  xs: "none",
                  md:
                    i < items.length - 1 ? `1px solid ${vira.border}` : "none",
                },
                borderBottom: {
                  xs:
                    i < items.length - 1 ? `1px solid ${vira.border}` : "none",
                  md: "none",
                },
              }
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 0.5 }}
            >
              <StatusIcon status={item.status} />
              <Heading level="label">{item.title}</Heading>
              {item.tooltip ? (
                <Tooltip
                  title={item.tooltip}
                  arrow
                  enterDelay={200}
                  describeChild
                >
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "help",
                      color: "vira.subtle",
                    }}
                    aria-label={`${item.title} info`}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                  </Box>
                </Tooltip>
              ) : null}
            </Stack>
            <ViraText
              tone="subtle"
              sx={{ fontSize: 12, mb: item.actionLabel ? 1 : 0 }}
            >
              {item.detail}
            </ViraText>
            {item.actionLabel && item.onAction ? (
              <ViraLink variant="underline" onClick={item.onAction}>
                {item.actionLabel}
              </ViraLink>
            ) : null}
          </Box>
        ))}
      </Stack>
    </ContentCard>
  )
}
